import { useState, useMemo, useCallback, useRef, useEffect } from "react";

// ═══════════════════════════════════════════════════════════════
// PERSONAL STACK CHECKER
// Add your dog's supplements → see cumulative nutrient loads
// ═══════════════════════════════════════════════════════════════

// Nutrient safe upper limits per kg body weight per day
// Sources: NRC, AAFCO, veterinary pharmacology literature
const NUTRIENT_LIMITS = {
  "Vitamin A": { unit: "IU", perKg: 100, warn: 80, desc: "Fat-soluble; accumulates in liver. Toxicity causes bone pain, peeling skin, liver damage." },
  "Vitamin D3": { unit: "IU", perKg: 4.5, warn: 3, desc: "VERY narrow safety margin. Excess causes hypercalcemia, kidney failure, death." },
  "Vitamin E": { unit: "IU", perKg: 10, warn: 8, desc: "Generally safe but very high doses impair clotting. Caution pre-surgery." },
  "Vitamin K": { unit: "mcg", perKg: 50, warn: 40, desc: "Fat-soluble. Usually safe but high doses can affect coagulation balance." },
  "Vitamin B1 (Thiamine)": { unit: "mg", perKg: 5, warn: 4, desc: "Water-soluble; excess excreted. Very safe." },
  "Vitamin B2 (Riboflavin)": { unit: "mg", perKg: 5, warn: 4, desc: "Water-soluble; very safe. Excess excreted in urine." },
  "Vitamin B3 (Niacin)": { unit: "mg", perKg: 5, warn: 4, desc: "High doses can stress liver, especially nicotinamide form." },
  "Vitamin B5": { unit: "mg", perKg: 10, warn: 8, desc: "Water-soluble; very safe at normal doses." },
  "Vitamin B6": { unit: "mg", perKg: 5, warn: 4, desc: "Extremely high doses can cause neuropathy." },
  "Vitamin B7 (Biotin)": { unit: "mcg", perKg: 20, warn: 15, desc: "Water-soluble; very safe." },
  "Vitamin B9 (Folic Acid)": { unit: "mcg", perKg: 20, warn: 15, desc: "Water-soluble; generally safe." },
  "Vitamin B12": { unit: "mcg", perKg: 100, warn: 80, desc: "Water-soluble; very safe even at high doses." },
  "Vitamin C": { unit: "mg", perKg: 50, warn: 40, desc: "Dogs synthesize their own. Excess causes diarrhea and oxalate stone risk." },
  "Calcium": { unit: "mg", perKg: 120, warn: 100, desc: "Excess causes skeletal issues in puppies, calcification in adults. Balance with phosphorus." },
  "Phosphorus": { unit: "mg", perKg: 90, warn: 70, desc: "Must restrict in kidney disease. Excess accelerates CKD progression." },
  "Iron": { unit: "mg", perKg: 15, warn: 10, desc: "Excess is toxic. Accumulates in liver. Causes oxidative damage." },
  "Zinc": { unit: "mg", perKg: 5, warn: 4, desc: "Excess depletes copper and causes GI upset. Some breeds need more." },
  "Copper": { unit: "mg", perKg: 0.3, warn: 0.2, desc: "VERY narrow margin. Toxic to some breeds. Accumulates in liver." },
  "Selenium": { unit: "mcg", perKg: 5, warn: 4, desc: "Narrow safety margin. Excess causes selenosis — hair loss, nail brittleness, neuro issues." },
  "Iodine": { unit: "mcg", perKg: 20, warn: 15, desc: "Excess disrupts thyroid. Both hypo and hyperthyroidism possible." },
  "Magnesium": { unit: "mg", perKg: 20, warn: 15, desc: "Generally safe. Excess causes loose stool. Kidney disease requires caution." },
  "Manganese": { unit: "mg", perKg: 2, warn: 1.5, desc: "Generally safe at normal doses." },
  "EPA (Omega-3)": { unit: "mg", perKg: 100, warn: 80, desc: "High doses impair platelet function. Caution pre-surgery and with blood thinners." },
  "DHA (Omega-3)": { unit: "mg", perKg: 100, warn: 80, desc: "Same cautions as EPA. Combined EPA+DHA limit more important." },
  "EPA+DHA Combined": { unit: "mg", perKg: 150, warn: 120, desc: "Total omega-3 load. High doses increase bleeding risk and may raise ALT." },
  "Curcumin": { unit: "mg", perKg: 25, warn: 20, desc: "Antiplatelet effects at high doses. Stimulates gallbladder. Caution with bile duct issues." },
  "Silymarin": { unit: "mg", perKg: 15, warn: 12, desc: "Generally very safe. Rare loose stool." },
  "Glucosamine": { unit: "mg", perKg: 25, warn: 20, desc: "May raise blood sugar in diabetic dogs. Monitor glucose." },
  "Chondroitin": { unit: "mg", perKg: 18, warn: 15, desc: "Generally safe. Synergistic with glucosamine." },
  "CoQ10": { unit: "mg", perKg: 5, warn: 4, desc: "Very safe. Ubiquinol form preferred for absorption." },
  "Taurine": { unit: "mg", perKg: 60, warn: 50, desc: "Very safe at normal doses. Critical for heart function." },
  "L-Carnitine": { unit: "mg", perKg: 120, warn: 100, desc: "Generally safe. May cause fishy odor at high doses." },
  "CBD": { unit: "mg", perKg: 4, warn: 3, desc: "CYP450 inhibitor. Raises ALP. Monitor liver with chronic use. Affects many drug levels." },
};

// Supplement nutrient profiles — what each supplement CONTAINS
// This maps each supplement ID to its typical nutrient contributions per standard dose
const SUPPLEMENT_NUTRIENTS = {
  "S001": { name: "Vitamin A (Retinol)", nutrients: { "Vitamin A": { perUnit: 5000, unit: "IU", perLabel: "per capsule" } } },
  "S002": { name: "Vitamin B1 (Thiamine)", nutrients: { "Vitamin B1 (Thiamine)": { perUnit: 50, unit: "mg", perLabel: "per tablet" } } },
  "S003": { name: "Vitamin B2 (Riboflavin)", nutrients: { "Vitamin B2 (Riboflavin)": { perUnit: 25, unit: "mg", perLabel: "per tablet" } } },
  "S004": { name: "Vitamin B3 (Niacin)", nutrients: { "Vitamin B3 (Niacin)": { perUnit: 50, unit: "mg", perLabel: "per tablet" } } },
  "S005": { name: "Vitamin B5", nutrients: { "Vitamin B5": { perUnit: 100, unit: "mg", perLabel: "per tablet" } } },
  "S006": { name: "Vitamin B6 (Pyridoxine)", nutrients: { "Vitamin B6": { perUnit: 25, unit: "mg", perLabel: "per tablet" } } },
  "S007": { name: "Vitamin B7 (Biotin)", nutrients: { "Vitamin B7 (Biotin)": { perUnit: 1000, unit: "mcg", perLabel: "per tablet" } } },
  "S008": { name: "Vitamin B9 (Folic Acid)", nutrients: { "Vitamin B9 (Folic Acid)": { perUnit: 400, unit: "mcg", perLabel: "per tablet" } } },
  "S009": { name: "Vitamin B12 (Cobalamin)", nutrients: { "Vitamin B12": { perUnit: 1000, unit: "mcg", perLabel: "per tablet" } } },
  "S010": { name: "Vitamin C", nutrients: { "Vitamin C": { perUnit: 500, unit: "mg", perLabel: "per tablet" } } },
  "S011": { name: "Vitamin D3", nutrients: { "Vitamin D3": { perUnit: 1000, unit: "IU", perLabel: "per drop/capsule" } } },
  "S012": { name: "Vitamin E", nutrients: { "Vitamin E": { perUnit: 200, unit: "IU", perLabel: "per capsule" } } },
  "S013": { name: "Vitamin K1", nutrients: { "Vitamin K": { perUnit: 2500, unit: "mcg", perLabel: "per tablet" } } },
  "S015": { name: "Calcium", nutrients: { "Calcium": { perUnit: 500, unit: "mg", perLabel: "per tablet" } } },
  "S016": { name: "Phosphorus", nutrients: { "Phosphorus": { perUnit: 200, unit: "mg", perLabel: "per tablet" } } },
  "S017": { name: "Magnesium", nutrients: { "Magnesium": { perUnit: 200, unit: "mg", perLabel: "per dose" } } },
  "S020": { name: "Iron", nutrients: { "Iron": { perUnit: 25, unit: "mg", perLabel: "per tablet" } } },
  "S021": { name: "Zinc", nutrients: { "Zinc": { perUnit: 25, unit: "mg", perLabel: "per tablet" } } },
  "S022": { name: "Selenium", nutrients: { "Selenium": { perUnit: 50, unit: "mcg", perLabel: "per tablet" } } },
  "S023": { name: "Copper", nutrients: { "Copper": { perUnit: 2, unit: "mg", perLabel: "per tablet" } } },
  "S024": { name: "Manganese", nutrients: { "Manganese": { perUnit: 5, unit: "mg", perLabel: "per tablet" } } },
  "S025": { name: "Iodine", nutrients: { "Iodine": { perUnit: 150, unit: "mcg", perLabel: "per dose" } } },
  "S029": { name: "L-Glutamine", nutrients: {} },
  "S030": { name: "L-Carnitine", nutrients: { "L-Carnitine": { perUnit: 500, unit: "mg", perLabel: "per capsule" } } },
  "S031": { name: "Taurine", nutrients: { "Taurine": { perUnit: 500, unit: "mg", perLabel: "per capsule" } } },
  "S034": { name: "NAC", nutrients: {} },
  "S035": { name: "SAMe", nutrients: {} },
  "S041": { name: "Fish Oil (EPA/DHA)", nutrients: {
    "EPA (Omega-3)": { perUnit: 360, unit: "mg", perLabel: "per softgel (1000mg)" },
    "DHA (Omega-3)": { perUnit: 240, unit: "mg", perLabel: "per softgel (1000mg)" },
    "EPA+DHA Combined": { perUnit: 600, unit: "mg", perLabel: "per softgel (1000mg)" },
    "Vitamin A": { perUnit: 0, unit: "IU", perLabel: "" },
    "Vitamin D3": { perUnit: 0, unit: "IU", perLabel: "" },
  }},
  "S042": { name: "Krill Oil", nutrients: {
    "EPA (Omega-3)": { perUnit: 150, unit: "mg", perLabel: "per softgel" },
    "DHA (Omega-3)": { perUnit: 90, unit: "mg", perLabel: "per softgel" },
    "EPA+DHA Combined": { perUnit: 240, unit: "mg", perLabel: "per softgel" },
    "Vitamin A": { perUnit: 100, unit: "IU", perLabel: "naturally occurring" },
  }},
  "S046": { name: "Coconut Oil (MCTs)", nutrients: {} },
  "S047": { name: "Cod Liver Oil", nutrients: {
    "EPA (Omega-3)": { perUnit: 200, unit: "mg", perLabel: "per tsp" },
    "DHA (Omega-3)": { perUnit: 220, unit: "mg", perLabel: "per tsp" },
    "EPA+DHA Combined": { perUnit: 420, unit: "mg", perLabel: "per tsp" },
    "Vitamin A": { perUnit: 4500, unit: "IU", perLabel: "per tsp — HIGH" },
    "Vitamin D3": { perUnit: 450, unit: "IU", perLabel: "per tsp" },
  }},
  "S048": { name: "Glucosamine HCl", nutrients: { "Glucosamine": { perUnit: 500, unit: "mg", perLabel: "per tablet" } } },
  "S049": { name: "Chondroitin Sulfate", nutrients: { "Chondroitin": { perUnit: 400, unit: "mg", perLabel: "per tablet" } } },
  "S050": { name: "MSM", nutrients: {} },
  "S052": { name: "Green-Lipped Mussel", nutrients: {
    "EPA+DHA Combined": { perUnit: 50, unit: "mg", perLabel: "per dose (variable)" },
    "Glucosamine": { perUnit: 30, unit: "mg", perLabel: "naturally occurring" },
    "Chondroitin": { perUnit: 20, unit: "mg", perLabel: "naturally occurring" },
  }},
  "S068": { name: "Milk Thistle (Silymarin)", nutrients: { "Silymarin": { perUnit: 175, unit: "mg", perLabel: "per capsule (standardized)" } } },
  "S075": { name: "Turmeric / Curcumin", nutrients: { "Curcumin": { perUnit: 250, unit: "mg", perLabel: "per capsule (standardized)" } } },
  "S076": { name: "CoQ10", nutrients: { "CoQ10": { perUnit: 100, unit: "mg", perLabel: "per softgel" } } },
  "S085": { name: "Lutein", nutrients: {} },
  "S088": { name: "Astaxanthin", nutrients: {} },
  "S091": { name: "MCT Oil", nutrients: {} },
  "S095": { name: "Medicinal Mushroom Blend", nutrients: { "Vitamin D3": { perUnit: 200, unit: "IU", perLabel: "naturally occurring (varies)" } } },
  "S096": { name: "Turkey Tail Mushroom", nutrients: {} },
  "S097": { name: "Reishi Mushroom", nutrients: {} },
  "S107": { name: "CBD Oil", nutrients: { "CBD": { perUnit: 10, unit: "mg", perLabel: "per ml (varies by product)" } } },
  "S117": { name: "Kelp / Bladderwrack", nutrients: {
    "Iodine": { perUnit: 300, unit: "mcg", perLabel: "per dose — HIGHLY VARIABLE" },
    "Iron": { perUnit: 2, unit: "mg", perLabel: "trace" },
  }},
  "S135": { name: "Senior Multivitamin", nutrients: {
    "Vitamin A": { perUnit: 2500, unit: "IU", perLabel: "per serving" },
    "Vitamin D3": { perUnit: 200, unit: "IU", perLabel: "per serving" },
    "Vitamin E": { perUnit: 50, unit: "IU", perLabel: "per serving" },
    "Vitamin B1 (Thiamine)": { perUnit: 5, unit: "mg", perLabel: "per serving" },
    "Vitamin B2 (Riboflavin)": { perUnit: 5, unit: "mg", perLabel: "per serving" },
    "Vitamin B6": { perUnit: 5, unit: "mg", perLabel: "per serving" },
    "Vitamin B12": { perUnit: 25, unit: "mcg", perLabel: "per serving" },
    "Vitamin C": { perUnit: 50, unit: "mg", perLabel: "per serving" },
    "Calcium": { perUnit: 100, unit: "mg", perLabel: "per serving" },
    "Iron": { perUnit: 5, unit: "mg", perLabel: "per serving" },
    "Zinc": { perUnit: 10, unit: "mg", perLabel: "per serving" },
    "Copper": { perUnit: 0.5, unit: "mg", perLabel: "per serving" },
    "Selenium": { perUnit: 20, unit: "mcg", perLabel: "per serving" },
    "Manganese": { perUnit: 2, unit: "mg", perLabel: "per serving" },
    "Glucosamine": { perUnit: 250, unit: "mg", perLabel: "per serving" },
    "EPA+DHA Combined": { perUnit: 100, unit: "mg", perLabel: "per serving" },
  }},
  "S140": { name: "Organ Meat Supplement", nutrients: {
    "Vitamin A": { perUnit: 3000, unit: "IU", perLabel: "per serving (liver-heavy)" },
    "Iron": { perUnit: 8, unit: "mg", perLabel: "per serving" },
    "Vitamin B12": { perUnit: 50, unit: "mcg", perLabel: "per serving" },
    "Copper": { perUnit: 1, unit: "mg", perLabel: "per serving" },
  }},
  "S144": { name: "Kelp Powder", nutrients: {
    "Iodine": { perUnit: 250, unit: "mcg", perLabel: "per 1/4 tsp — VARIABLE" },
  }},
  "S219": { name: "Black Seed Oil", nutrients: {} },
  "S220": { name: "Chaga Mushroom", nutrients: {} },
  "S221": { name: "Maitake Mushroom", nutrients: {} },
};

// Build supplement options list from SUPPLEMENT_NUTRIENTS
const STACK_SUPPLEMENTS = Object.entries(SUPPLEMENT_NUTRIENTS).map(([id, data]) => ({
  id, name: data.name, hasNutrients: Object.keys(data.nutrients).length > 0,
})).sort((a, b) => a.name.localeCompare(b.name));

function getStatusColor(pct) {
  if (pct >= 100) return { bg: "#4a0010", border: "#ff1744", text: "#ff8a80", label: "OVER LIMIT", emoji: "🚨" };
  if (pct >= 80) return { bg: "#3e1400", border: "#ff6d00", text: "#ffab40", label: "HIGH", emoji: "⚠️" };
  if (pct >= 50) return { bg: "#2a2400", border: "#ffd600", text: "#ffff00", label: "MODERATE", emoji: "📊" };
  return { bg: "#0d1f15", border: "#22c55e", text: "#86efac", label: "SAFE", emoji: "✅" };
}

export default function StackChecker() {
  const [dogName, setDogName] = useState("");
  const [dogWeight, setDogWeight] = useState("");
  const [stack, setStack] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [editingDoses, setEditingDoses] = useState({});
  const searchRef = useRef(null);

  const weightKg = dogWeight ? parseFloat(dogWeight) / 2.205 : 0;

  const addSupplement = useCallback((supp) => {
    if (stack.find(s => s.id === supp.id)) return;
    setStack(prev => [...prev, { ...supp, doses: 1 }]);
    setSearchText("");
    setShowSearch(false);
  }, [stack]);

  const removeSupplement = useCallback((id) => {
    setStack(prev => prev.filter(s => s.id !== id));
  }, []);

  const updateDoses = useCallback((id, doses) => {
    setStack(prev => prev.map(s => s.id === id ? { ...s, doses: parseFloat(doses) || 0 } : s));
  }, []);

  const filteredSupps = useMemo(() => {
    if (!searchText) return STACK_SUPPLEMENTS.slice(0, 15);
    const q = searchText.toLowerCase();
    return STACK_SUPPLEMENTS.filter(s => s.name.toLowerCase().includes(q)).slice(0, 10);
  }, [searchText]);

  // Calculate cumulative nutrient loads
  const nutrientTotals = useMemo(() => {
    const totals = {};
    for (const item of stack) {
      const profile = SUPPLEMENT_NUTRIENTS[item.id];
      if (!profile) continue;
      for (const [nutrient, info] of Object.entries(profile.nutrients)) {
        if (!totals[nutrient]) totals[nutrient] = { total: 0, unit: info.unit, sources: [] };
        const amount = info.perUnit * item.doses;
        totals[nutrient].total += amount;
        totals[nutrient].sources.push({ name: profile.name, amount, perLabel: info.perLabel, doses: item.doses });
      }
    }
    return totals;
  }, [stack]);

  // Calculate percentages against safe limits
  const nutrientAnalysis = useMemo(() => {
    if (!weightKg) return [];
    const results = [];
    for (const [nutrient, data] of Object.entries(nutrientTotals)) {
      const limit = NUTRIENT_LIMITS[nutrient];
      if (!limit || data.total === 0) continue;
      const maxDaily = limit.perKg * weightKg;
      const warnDaily = limit.warn * weightKg;
      const pct = (data.total / maxDaily) * 100;
      results.push({
        nutrient, total: data.total, unit: data.unit,
        maxDaily, warnDaily, pct,
        sources: data.sources, desc: limit.desc,
        status: getStatusColor(pct),
      });
    }
    results.sort((a, b) => b.pct - a.pct);
    return results;
  }, [nutrientTotals, weightKg]);

  const overLimitCount = nutrientAnalysis.filter(n => n.pct >= 100).length;
  const highCount = nutrientAnalysis.filter(n => n.pct >= 80 && n.pct < 100).length;

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(170deg, #0a0f1a 0%, #0d1926 40%, #0f1f2e 100%)",
      color: "#e0e8f0",
      fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    }}>
      {/* HEADER */}
      <div style={{
        padding: "28px 20px 20px",
        background: "linear-gradient(135deg, #0d2137 0%, #162d44 50%, #1a3550 100%)",
        borderBottom: "1px solid #1e4060", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 30% 50%, rgba(56,189,248,0.06) 0%, transparent 60%)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <div style={{ fontSize: 28 }}>⚖️</div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", color: "#f0f6fc" }}>
              Stack Checker
            </h1>
          </div>
          <p style={{ margin: 0, fontSize: 15, color: "#7eb8d8", lineHeight: 1.5 }}>
            Add your dog's supplements → see cumulative nutrient loads → catch overlaps before they cause harm
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 16px 80px" }}>

        {/* DOG INFO */}
        <div style={{
          padding: "20px", borderRadius: 14,
          background: "rgba(255,255,255,0.02)", border: "1px solid rgba(56,189,248,0.1)",
          marginBottom: 20, display: "flex", gap: 12, flexWrap: "wrap",
        }}>
          <div style={{ flex: "1 1 140px" }}>
            <label style={labelStyle}>Dog's Name</label>
            <input value={dogName} onChange={e => setDogName(e.target.value)}
              placeholder="e.g. Nilla" style={inputStyle} />
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <label style={labelStyle}>Weight (lbs)</label>
            <input type="number" value={dogWeight} onChange={e => setDogWeight(e.target.value)}
              placeholder="e.g. 22" style={inputStyle} />
          </div>
          {weightKg > 0 && (
            <div style={{ flex: "1 1 140px", display: "flex", alignItems: "flex-end", paddingBottom: 2 }}>
              <div style={{ fontSize: 14, color: "#5a8098" }}>
                = {weightKg.toFixed(1)} kg
              </div>
            </div>
          )}
        </div>

        {/* ADD SUPPLEMENTS */}
        <div style={{
          padding: "20px", borderRadius: 14,
          background: "rgba(255,255,255,0.02)", border: "1px solid rgba(56,189,248,0.1)",
          marginBottom: 20,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#c0d4e4" }}>
              🧴 {dogName ? `${dogName}'s` : "Your Dog's"} Supplement Stack
            </div>
            <button onClick={() => setShowSearch(!showSearch)} style={{
              padding: "8px 18px", borderRadius: 8, border: "1px solid rgba(56,189,248,0.3)",
              background: "rgba(56,189,248,0.1)", color: "#7dd3fc", fontSize: 14,
              cursor: "pointer", fontWeight: 600,
            }}>+ Add</button>
          </div>

          {/* SEARCH DROPDOWN */}
          {showSearch && (
            <div style={{ marginBottom: 16 }}>
              <input ref={searchRef} autoFocus value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="Search supplements..."
                style={{ ...inputStyle, marginBottom: 8 }} />
              <div style={{
                maxHeight: 240, overflowY: "auto", borderRadius: 10,
                border: "1px solid #1e3a55", background: "rgba(10,15,26,0.95)",
              }}>
                {filteredSupps.map(s => (
                  <div key={s.id} onClick={() => addSupplement(s)} style={{
                    padding: "12px 16px", cursor: "pointer",
                    borderBottom: "1px solid #1a2a3a",
                    color: stack.find(x => x.id === s.id) ? "#4a6a80" : "#c0d4e4",
                    fontSize: 15, display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <span>{s.name}</span>
                    {s.hasNutrients && <span style={{ fontSize: 11, color: "#38bdf8", opacity: 0.5 }}>has nutrient data</span>}
                    {stack.find(x => x.id === s.id) && <span style={{ fontSize: 12, color: "#4a6a80" }}>✓ added</span>}
                  </div>
                ))}
                {filteredSupps.length === 0 && (
                  <div style={{ padding: "16px", color: "#4a6a80", fontSize: 14, textAlign: "center" }}>
                    No matches found
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STACK LIST */}
          {stack.length === 0 && !showSearch && (
            <div style={{ textAlign: "center", padding: "30px 20px", color: "#4a6a80" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
              <div style={{ fontSize: 15 }}>No supplements added yet</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Tap "+ Add" to start building the stack</div>
            </div>
          )}

          {stack.map(item => {
            const profile = SUPPLEMENT_NUTRIENTS[item.id];
            const nutrientCount = profile ? Object.keys(profile.nutrients).length : 0;
            return (
              <div key={item.id} style={{
                padding: "14px 16px", marginBottom: 6, borderRadius: 10,
                background: "rgba(255,255,255,0.03)", border: "1px solid #1a3050",
                display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              }}>
                <div style={{ flex: "1 1 160px" }}>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "#d0dce8" }}>{item.name}</div>
                  <div style={{ fontSize: 12, color: "#5a8098", marginTop: 2 }}>
                    {nutrientCount > 0 ? `Tracks ${nutrientCount} nutrient${nutrientCount > 1 ? "s" : ""}` : "No nutrient overlap data"}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <label style={{ fontSize: 13, color: "#6b8ba4" }}>Doses/day:</label>
                  <input type="number" min="0" step="0.5" value={item.doses}
                    onChange={e => updateDoses(item.id, e.target.value)}
                    style={{ ...inputStyle, width: 70, textAlign: "center", padding: "8px" }} />
                </div>
                <button onClick={() => removeSupplement(item.id)} style={{
                  width: 32, height: 32, borderRadius: 8, border: "1px solid #3a1520",
                  background: "rgba(239,68,68,0.08)", color: "#f87171",
                  cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
                }}>✕</button>
              </div>
            );
          })}
        </div>

        {/* ANALYSIS RESULTS */}
        {stack.length > 0 && weightKg > 0 && nutrientAnalysis.length > 0 && (
          <div style={{
            padding: "20px", borderRadius: 14,
            background: "rgba(255,255,255,0.02)", border: "1px solid rgba(56,189,248,0.1)",
            marginBottom: 20,
          }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#c0d4e4", marginBottom: 4 }}>
              📊 Nutrient Load Analysis {dogName ? `for ${dogName}` : ""} ({dogWeight} lbs)
            </div>

            {/* Summary badges */}
            {(overLimitCount > 0 || highCount > 0) && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16, marginTop: 12, flexWrap: "wrap" }}>
                {overLimitCount > 0 && (
                  <div style={{
                    padding: "8px 16px", borderRadius: 10,
                    background: "#4a0010", border: "1px solid #ff174440",
                    fontSize: 14, fontWeight: 700, color: "#ff8a80",
                  }}>🚨 {overLimitCount} nutrient{overLimitCount > 1 ? "s" : ""} OVER safe limit</div>
                )}
                {highCount > 0 && (
                  <div style={{
                    padding: "8px 16px", borderRadius: 10,
                    background: "#3e1400", border: "1px solid #ff6d0040",
                    fontSize: 14, fontWeight: 700, color: "#ffab40",
                  }}>⚠️ {highCount} nutrient{highCount > 1 ? "s" : ""} approaching limit</div>
                )}
              </div>
            )}

            {/* Nutrient bars */}
            {nutrientAnalysis.map(n => (
              <div key={n.nutrient} style={{
                marginBottom: 10, padding: "14px 16px", borderRadius: 10,
                background: n.status.bg, border: `1px solid ${n.status.border}25`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span>{n.status.emoji}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: n.status.text }}>{n.nutrient}</span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: n.status.text }}>
                    {Math.round(n.pct)}%
                  </div>
                </div>

                {/* Progress bar */}
                <div style={{
                  height: 8, borderRadius: 4, background: "rgba(255,255,255,0.05)",
                  marginBottom: 8, overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%", borderRadius: 4,
                    width: `${Math.min(n.pct, 100)}%`,
                    background: n.pct >= 100
                      ? "linear-gradient(90deg, #ff6d00, #ff1744)"
                      : n.pct >= 80
                        ? "linear-gradient(90deg, #ffd600, #ff6d00)"
                        : n.pct >= 50
                          ? "linear-gradient(90deg, #76ff03, #ffd600)"
                          : "#22c55e",
                    transition: "width 0.5s ease",
                  }} />
                </div>

                <div style={{ fontSize: 14, color: "rgba(200,200,210,0.6)", marginBottom: 6 }}>
                  <strong style={{ color: n.status.text }}>{n.total.toFixed(1)} {n.unit}</strong> daily
                  <span style={{ margin: "0 6px", opacity: 0.4 }}>|</span>
                  Safe limit: {n.maxDaily.toFixed(1)} {n.unit}/day for {dogWeight} lbs
                </div>

                {/* Sources breakdown */}
                <div style={{ fontSize: 13, color: "rgba(200,200,210,0.45)" }}>
                  {n.sources.map((src, i) => (
                    <span key={i}>
                      {src.name}: {src.amount.toFixed(1)} {n.unit} ({src.doses}x{src.perLabel ? ` ${src.perLabel}` : ""})
                      {i < n.sources.length - 1 ? " + " : ""}
                    </span>
                  ))}
                </div>

                {n.pct >= 80 && (
                  <div style={{
                    marginTop: 8, padding: "8px 12px", borderRadius: 6,
                    background: `${n.status.border}10`, fontSize: 13,
                    color: n.status.text, lineHeight: 1.5,
                  }}>
                    {n.desc}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* NO WEIGHT WARNING */}
        {stack.length > 0 && !weightKg && (
          <div style={{
            padding: "20px", borderRadius: 14, textAlign: "center",
            background: "rgba(255,189,0,0.04)", border: "1px solid rgba(255,189,0,0.15)",
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⚖️</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#ffd600", marginBottom: 4 }}>
              Enter your dog's weight to see the analysis
            </div>
            <div style={{ fontSize: 14, color: "#8b9aa8" }}>
              Safe limits are calculated per kilogram of body weight — the same supplement dose that's fine for a 70lb dog can be dangerous for a 22lb dog.
            </div>
          </div>
        )}

        {/* NUTRIENT NOTE */}
        <div style={{
          marginTop: 20, padding: "16px 20px", borderRadius: 10,
          background: "rgba(255,255,255,0.015)", border: "1px solid rgba(255,255,255,0.04)",
        }}>
          <div style={{ fontSize: 14, color: "rgba(200,190,175,0.45)", lineHeight: 1.7, textAlign: "center" }}>
            <strong style={{ color: "rgba(200,190,175,0.6)" }}>How to read this:</strong> Nutrient amounts shown are based on
            <em> typical supplement formulations</em>. Your specific products may differ — always check your labels and
            enter the actual amounts for the most accurate analysis. Supplements without nutrient data are still tracked
            in your stack for reference but won't appear in the load analysis.
          </div>
        </div>

        {/* CUSTOM ENTRY HINT */}
        <div style={{
          marginTop: 12, padding: "16px 20px", borderRadius: 10,
          background: "rgba(56,189,248,0.03)", border: "1px solid rgba(56,189,248,0.08)",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 14, color: "#5a8098", lineHeight: 1.6 }}>
            💡 <strong>Tip:</strong> If your product delivers different amounts than shown, adjust the "doses/day" field
            to approximate. For example, if your fish oil softgel has 500mg EPA+DHA instead of 600mg, the ratio
            still shows you where you stand. We're building custom product entry for a future update.
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{
        padding: "18px 20px", background: "rgba(239,68,68,0.05)",
        borderTop: "1px solid #2a1520", textAlign: "center",
      }}>
        <div style={{ fontSize: 13, color: "#8b6070", maxWidth: 700, margin: "0 auto", lineHeight: 1.5 }}>
          ⚕️ This tool provides estimates based on typical supplement formulations. Always verify with your
          specific product labels and consult your veterinarian before changing any supplement regimen.
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: "block", fontSize: 13, fontWeight: 600,
  color: "#6b8ba4", marginBottom: 6,
};

const inputStyle = {
  width: "100%", padding: "11px 14px", borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.08)",
  background: "rgba(255,255,255,0.04)", color: "#e0e8f0",
  fontSize: 16, boxSizing: "border-box", outline: "none",
};
